/**
 * Measurement points of the brand booth overlay. Events are buffered, sent in batches and
 * flushed with sendBeacon when the page is left. Tracking must never disturb the overlay,
 * so all errors are swallowed.
 */

export type BoothEventName =
  | 'overlay_opened'
  | 'overlay_closed'
  | 'video_started'
  | 'tab_changed'
  | 'carousel_next'
  | 'suggestion_shown'
  | 'suggestion_added'
  | 'item_expanded'
  | 'basket_added'
  | 'sample_added'
  | 'media_downloaded'
  | 'contact_sent'
  | 'listing_request_opened'
  | 'feedback_sent';

interface BufferedBoothEvent {
  event: BoothEventName;
  exhibitor_id: number;
  payload?: Record<string, unknown>;
  client_time: string;
}

const ENDPOINT = '/api/onlineshop/vts-events';
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_THRESHOLD = 20;

let buffer: BufferedBoothEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function clearTimer(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

function takeBody(): string | null {
  if (buffer.length === 0) {
    return null;
  }
  const body = JSON.stringify({ events: buffer });
  buffer = [];
  clearTimer();
  return body;
}

function sendWithFetch(body: string): void {
  try {
    fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => undefined);
  } catch (e) {
    // Ignore: tracking must not break the page.
  }
}

/** Sends the buffer with fetch and empties it. */
export function flushBoothEvents(): void {
  const body = takeBody();
  if (body !== null) {
    sendWithFetch(body);
  }
}

/** Sends the buffer with sendBeacon (fetch as fallback), used when the page is hidden. */
function flushWithBeacon(): void {
  const body = takeBody();
  if (body === null) {
    return;
  }
  try {
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      if (
        navigator.sendBeacon(
          ENDPOINT,
          new Blob([body], { type: 'application/json' })
        )
      ) {
        return;
      }
    }
  } catch (e) {
    // Fall through to fetch.
  }
  sendWithFetch(body);
}

/** Buffers one event; flushes after 5 seconds or at 20 buffered events. */
export function trackBoothEvent(
  event: BoothEventName,
  exhibitorId: number,
  payload?: Record<string, unknown>
): void {
  try {
    buffer.push({
      event,
      exhibitor_id: exhibitorId,
      payload,
      client_time: new Date().toISOString(),
    });
    if (buffer.length >= FLUSH_THRESHOLD) {
      flushBoothEvents();
    } else if (timer === null) {
      timer = setTimeout(() => {
        timer = null;
        flushBoothEvents();
      }, FLUSH_INTERVAL_MS);
    }
  } catch (e) {
    // Ignore: tracking must not break the page.
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushWithBeacon);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushWithBeacon();
    }
  });
}
